import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    isPlaying: false,
    playingId: null,
    restartId: null,
    playingTracks: [],
    selectedId: null,
    selectedName: '',
    selectedType: '',
    createPlaylistType: 'default',
    shareSystemCreatedPlaylist: false,
    selectedItem: [],
    config: {
        id: null,
        name: '',
        type: 'default',
        playlist_type: 'default',
        data: [],
        playingId: null,
        restartId: null,
        isRepeat: false,
        isShuffle: false,
        isPlaylist: false,
        isPlaylistMix: false,
        shouldPlay: null,
        playlist_ended: false,
        is_system_playlist: false
    },
};

const PlayerSlice = createSlice({
    name: "player",
    initialState,
    reducers: {
        togglePlayPause: (state, action) => {
            state.isPlaying = !state.isPlaying;
        },
        pauseAllTracks: (state, action) => {
            state.isPlaying = false;
        },
        playAllTracks: (state, action) => {
            state.isPlaying = true;
        },
        addSingleTrack(state, action) {
            if (state.config.type === 'playlist' || state.config.type === 'mix') {
                state.playingTracks = [];
            }
            const { item } = action.payload;
            const audioCategories = ["relaxation", "articles", "stories"];
            // 🎯 Single-selection rules 
            const isLullaBy = item.soundcategory === 163;
            const isMusic = item.soundcategory === 164;
            const isAudioType = audioCategories.includes(item.type);
            if (isMusic || isLullaBy) {
                state.playingTracks = state.playingTracks.filter(t => t.soundcategory !== 163 && t.soundcategory !== 164);
            } else if (isAudioType) {
                state.playingTracks = state.playingTracks.filter(t => !audioCategories.includes(t.type));
            }
            // Push to global list only once
            state.playingTracks.push(item);
        },
        addMultipleTracks: (state, action) => {
            const { item } = action.payload;
            const list = item?.sounds_list || []
            const modifiedArray = [];
            list?.forEach((item) => {
                const detail = item.sounds_detail?.[0];
                if (!detail) return;
                modifiedArray.push(detail);
            });
            state.playingTracks = modifiedArray;
        },
        removeSingleTrack: (state, action) => {
            const { item } = action.payload;

            // remove the exact track by id
            state.playingTracks = state.playingTracks.filter(t => t.id !== item.id);

            if (state.config?.shouldPlay === item?.id) state.config.shouldPlay = null;

            if (state.playingTracks.length == 0) {
                state.playingTracks = [];
                state.selectedItem = [];
                state.selectedId = null;
                state.config = {
                    id: null,
                    name: '',
                    type: 'default',
                    playlist_type: 'default',
                    data: [],
                    playingId: null,
                    restartId: null,
                    isRepeat: false,
                    isShuffle: false,
                    isPlaylist: false,
                    shouldPlay: null,
                    playlist_ended: false
                };
            }

            // (No need for category-specific filtering here,
            // because we only remove the selected item,
            // not all tracks of the same category)
        },
        removeAllTracks: (state, action) => {
            state.playingTracks = [];
            state.selectedItem = [];
            state.selectedId = null;
            state.config = {
                id: null,
                name: '',
                type: 'default',
                playlist_type: 'default',
                data: [],
                playingId: null,
                restartId: null,
                isRepeat: false,
                isShuffle: false,
                isPlaylist: false,
                playlist_ended: false,
                shouldPlay: null
            };
        },
        addPlaylistTracks: (state, action) => {
            const { data } = action.payload;
            const list = data;
            const modifiedArray = []
            list?.forEach((item) => {
                modifiedArray.push(item);
            });
            state.playingTracks = modifiedArray;
        },
        appendTracksToPlaylist: (state, action) => {
            const audioCategories = ["relaxation", "articles", "stories"];
            const config = state.config;
            let currentList = state.playingTracks;
            let newList = action.payload;
            const isAudioType = audioCategories.includes(newList[0].type);


            if (config.playlist_type === "music_lullaby" && isAudioType) {
                currentList = state.playingTracks.filter(t => !audioCategories.includes(t.type));
            }

            if (config.playlist_type === 'article_stories_relaxation' && (newList[0]?.soundcategory === 163 || newList[0]?.soundcategory === 164)) {
                currentList = state.playingTracks.filter(t => t.soundcategory !== 163 && t.soundcategory !== 164);
            }


            const updatedArray = [...currentList, ...newList];
            state.playingTracks = updatedArray;
        },
        setPlayingId: (state, action) => {
            state.playingId = action.payload;
        },
        restartTrack: (state, action) => {
            state.restartId = action.payload; // track id to restart
        },
        setConfig: (state, action) => {
            const config = state.config;
            const payload = action.payload;
            state.config = { ...config, ...payload };
        },
        setSelectedItem: (state, action) => {
            state.selectedItem = action.payload;
        },
        setSelectedId: (state, action) => {
            state.selectedId = action.payload;
        },
        setSelectedName: (state, action) => {
            state.selectedName = action.payload;
        },
        setSelectedType: (state, action) => {
            state.selectedType = action.payload;
        },
        setCreatePlaylistType: (state, action) => {
            state.createPlaylistType = action.payload;
        },
        setShareSystemCreatedPlaylist: (state, action) => {
            state.shareSystemCreatedPlaylist = action.payload;
        },
        resetConfig: (state) => {
            state.config = {
                id: null,
                name: '',
                type: 'default',
                playlist_type: 'default',
                data: [],
                playingId: null,
                restartId: null,
                isRepeat: false,
                isShuffle: false,
                isPlaylist: false,
                shouldPlay: null,
                playlist_ended: false
            };
        }
    }
});

export const { togglePlayPause, pauseAllTracks, playAllTracks, addSingleTrack, addMultipleTracks, removeSingleTrack, removeAllTracks, addPlaylistTracks, setPlayingId, restartTrack, setConfig, setSelectedItem, setSelectedId, setSelectedName, setSelectedType, resetConfig, appendTracksToPlaylist, setCreatePlaylistType, setShareSystemCreatedPlaylist } = PlayerSlice.actions;
export default PlayerSlice.reducer;
